import apiClient from '@/lib/api-client'
import type { User } from '@/types'

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  password_confirmation: string
  username: string
  first_name?: string
  last_name?: string
  accept_terms?: boolean
  plan_id: string
}

export interface AuthResponse {
  user: User
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  checkout_url?: string
  subscription_status?: string
}

interface AuthApiResponse {
  data: AuthResponse
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<User> {
    const response = await apiClient.post<AuthApiResponse>('/auth/login', credentials)
    const { data } = response.data

    // Store tokens
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    return data.user
  },

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post<AuthApiResponse>('/auth/register', userData)
    const { data } = response.data

    // Store tokens
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    return data
  },

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refresh_token: refreshToken })
      } catch (error) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  },

  async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return false

    try {
      const response = await apiClient.post<AuthApiResponse>('/auth/refresh', {
        refresh_token: refreshToken
      })
      const { data } = response.data

      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)

      return true
    } catch (error) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      return false
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ data: User }>('/auth/me')
    return response.data.data
  },
}